import { useMemo, type MouseEvent } from 'react';
import { marked } from 'marked';

// Lightweight markdown renderer for chat / QA assistant bubbles. Source is
// trusted (it comes from our own LLM call, not user input), so we forgo a
// full sanitizer — the side panel runs under MV3's default CSP which blocks
// inline scripts regardless.

marked.setOptions({
  gfm: true,
  breaks: true,
});

export function Markdown({
  source,
  trailingCaret,
  onChapterLink,
}: {
  source: string;
  trailingCaret?: boolean;
  // Called when a `chapter:<id>` link is clicked. The model emits these so it
  // can deep-link its answer to the chapter it drew from.
  onChapterLink?: (chapterId: string) => void;
}) {
  const html = useMemo(() => {
    try {
      const out = marked.parse(source, { async: false });
      return typeof out === 'string' ? out : '';
    } catch {
      return '';
    }
  }, [source]);

  function onClick(e: MouseEvent<HTMLDivElement>) {
    if (!onChapterLink) return;
    const anchor = (e.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href') ?? '';
    if (href.startsWith('chapter:')) {
      e.preventDefault();
      onChapterLink(href.slice('chapter:'.length));
    }
  }

  return (
    <div className="markdown-body text-sm">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />
      {trailingCaret && <span className="inline-block animate-pulse text-slate-400">▍</span>}
    </div>
  );
}
